'use client';

import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { isNumber } from '_internal/lang/isNumber';
import clsx from 'clsx';
import type { BoxProps } from 'components/Box';
import { Box } from 'components/Box';
import type { TextProps } from 'components/Text';
import { Text } from 'components/Text';
import type { InlineEditProps } from 'components/TextWithInlineEditing';
import { TextWithInlineEditing } from 'components/TextWithInlineEditing';
import { TooltipSubscriptionsProvider } from 'components/Tooltip';
import { useContextSelector } from 'use-context-selector';
import type { ThemeSpaces } from '@frameio/vapor-theme';
import styles from '../../Collections.module.css';
import {
  ListBoxItemContext,
  useIsListBoxItemCurrentTabStop,
} from './ListBoxItem';
import { useListStateContext } from './ListStateContext';

/**
 * Access the current list variant via context
 */

function useListVariant() {
  return useContextSelector(ListBoxItemContext, (context) => {
    return context.variant;
  });
}

export function ListItemLabelInlineEdit(props: InlineEditProps) {
  const isCurrentTabStop = useIsListBoxItemCurrentTabStop();

  const onMouse = (e: React.MouseEvent) => {
    if (isCurrentTabStop || props.enabled) {
      e.stopPropagation();
    }
  };

  return (
    <div
      onMouseDown={onMouse}
      onPointerDown={onMouse}
      style={{
        flexShrink: 1,
        flexGrow: 1,
        pointerEvents: isCurrentTabStop ? 'auto' : 'inherit',
      }}
    >
      <TextWithInlineEditing
        color="body"
        enabled={isCurrentTabStop}
        lineClamp={1}
        maxRowsWhileEditing={1}
        mode="button-with-double-click"
        variant="body"
        {...props}
      />
    </div>
  );
}

/**
 * Primary list label
 */

export function ListItemLabel({ children: label, ...props }: TextProps) {
  const variant = useListVariant();
  const state = useListStateContext();

  switch (variant) {
    case 'nav':
      return (
        <Text
          color="body"
          highlight={state?.queryState?.query}
          style={{
            flexShrink: 1,
            flexGrow: 1,
          }}
          truncate
          variant="body"
          {...props}
        >
          {label}
        </Text>
      );
    case 'list':
    default:
      return (
        <Text
          color="primary"
          fontWeight="medium"
          highlight={state?.queryState?.query}
          variant="primary"
          {...props}
        >
          {label}
        </Text>
      );
  }
}

/**
 * Description text
 */

export function ListItemDescription({ children, ...props }: TextProps) {
  const variant = useListVariant();
  const state = useListStateContext();

  switch (variant) {
    case 'nav':
      return (
        <Text
          color="secondary"
          enableTooltip
          highlight={state?.queryState?.query}
          truncate
          variant="body"
          {...props}
        >
          {children}
        </Text>
      );

    case 'list':
    default:
      return (
        <Text
          color="secondary"
          highlight={state?.queryState?.query}
          variant="primary"
          {...props}
        >
          {children}
        </Text>
      );
  }
}

/**
 * Meta text
 */

export function ListItemMeta({
  paddingTop: paddingTopProp,
  children,
  ...props
}: TextProps & Pick<BoxProps, 'paddingTop'>) {
  const variant = useListVariant();

  let paddingTop: ThemeSpaces = variant === 'nav' ? 0 : 4;

  if (typeof paddingTopProp !== 'undefined') {
    paddingTop = paddingTopProp;
  }

  const node = useMemo(() => {
    switch (variant) {
      case 'nav':
        return (
          <Text
            color="tertiary"
            data-meta
            enableTooltip
            style={{ whiteSpace: 'nowrap', paddingRight: '2px' }}
            truncate
            variant="caption"
            {...props}
          >
            {children}
          </Text>
        );
      case 'list':
      default:
        return (
          <Text color="tertiary" variant="secondary" {...props}>
            {children}
          </Text>
        );
    }
  }, [children, props, variant]);

  return <Box paddingTop={paddingTop}>{node}</Box>;
}

type Props = PropsWithChildren<{
  adornmentBefore?: React.ReactNode;
  adornmentAfter?: React.ReactNode;
  label?: React.ReactNode;
  description?: React.ReactNode;
  meta?: React.ReactNode;
  gap?: ThemeSpaces;
  'data-testid'?: string;
}> &
  Pick<BoxProps, 'className'>;

export function ListItemContent({
  adornmentBefore,
  adornmentAfter,
  description,
  label,
  meta,
  gap,
  'data-testid': dataTestId,
  className,
}: Props) {
  const variant = useListVariant();

  const listNode =
    typeof label === 'string' ? <ListItemLabel>{label}</ListItemLabel> : label;

  const descriptionNode =
    typeof description === 'string' ? (
      <ListItemDescription>{description}</ListItemDescription>
    ) : (
      description
    );

  const metaNode =
    typeof meta === 'string' || isNumber(meta) ? (
      <ListItemMeta>{meta}</ListItemMeta>
    ) : (
      meta
    );

  return (
    <div
      className={clsx(styles.itemContent, className)}
      data-testid={dataTestId}
      data-variant={variant}
      style={gap ? ({ '--gap': gap } as React.CSSProperties) : undefined}
    >
      {adornmentBefore}
      <TooltipSubscriptionsProvider>
        <div className={styles.itemContentText}>
          {listNode}
          {descriptionNode}
          {metaNode}
        </div>
      </TooltipSubscriptionsProvider>
      {adornmentAfter}
    </div>
  );
}
